import NewLayout from "@components/new-ui/Layout";
import React from "react";
import { CasestudyCard, CasestudyCardProps } from ".";
import { CaseStudyHerosection, CaseStudyInformation, FeatureList, Navbar, navLinks, OrganizationProfile } from "./alrannan-administrative-services";
import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";

const casestudyCardsDetails: CasestudyCardProps[] = [
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/alrannan-admin-service.png",
        title: "Success Story : Improved Transparency and Increased Conversions",
        sector: "Education",
        externalLink: "/case-study/alrannan-administrative-services",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/goireland.png",
        title: "Success Story : Improved Transparency and Increased Conversions",
        sector: "Education",
        externalLink: "/case-study/goireland",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/dubai-housekeeping.png",
        title: "Gallabox transforms Dubai Housekeeping's journey to service excellence on WhatsApp",
        sector: "Service",
        externalLink: "/case-study/dubai-housekeeping",
    },
];

const Nura: React.FC = () => {
    return (
        <NewLayout
            title="NURA Health Boosts simplifies operations with Gallabox | Case Study"
            description="Learn how NURA AI Health Screening streamlined WhatsApp communications, automated workflows, and improved employee productivity by 90% with Gallabox."
            canonical="https://gallabox.com/case-study/nura"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/case-study/nura/nura-og.png" }}
        >
            <CaseStudyHerosection
                title="How NURA Ai Health Screening Simplified Operations with Gallabox"
                imageUrl={"https://assets.gallabox.com/gb-home/case-study/nura/nura-hero.png"}
                redirectUrl={"/case-study"}
            />
            <StatsInfo
                statsInfo={[
                    { percentage: "90%", label: "improvement in employee productivity" },
                    { percentage: "80%", label: "growth in first-time conversions" },
                    { percentage: "60%", label: "improvement in customer satisfaction" },
                ]}
            />

            <section className="max-w-7xl mx-auto  md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px" }}>
                        <OrganizationProfile
                            companyProfileData={[{ label: "Category", value: ["Healthcare"] }]}
                            product={{
                                title: "Features Used",
                                productLink: [
                                    { link: "https://gallabox.com/whatsapp-broadcast", label: "WhatsApp Broadcast" },
                                    { link: "https://gallabox.com/whatsapp-chatbot", label: "WhatsApp Chatbot" },
                                    { link: "https://gallabox.com/whatsapp-flows", label: "WhatsApp Flows" },
                                    { link: "https://gallabox.com/whatsapp-shared-inbox", label: "Shared team inbox" },
                                ],
                            }}
                        />
                    </div>
                </div>
                <div className=" col-span-4 md:pl-8 px-4">
                    <div className="flex flex-col gap-y-12 ">
                        <section id="About NURA Ai Health Screening">
                            <p className="mt-2">
                                NURA Ai Health Screening is a healthcare company specializing in advanced AI-driven health checkups and diagnostic services. As patient inquiries
                                scaled, NURA needed a smarter way to manage lead nurturing, track team performance, and ensure timely communication.
                            </p>
                        </section>

                        <section id="Challenges">
                            <CaseStudyInformation subTitle={"Challenges before Gallabox"} className="mt-0" />

                            <p className="mt-2">NURA’s growth was being held back by three major challenges:</p>
                            <FeatureList
                                List={[
                                    {
                                        listItem: "No visibility into team performance on WhatsApp",
                                        nestedListItem: [
                                            "The hospital heavily relied on WhatsApp for communication, but managers had no clarity on how efficiently teams were responding or following up.",
                                        ],
                                    },
                                    {
                                        listItem: "Inability to send bulk messages",
                                        nestedListItem: [
                                            "Engaging leads at scale was nearly impossible. The platform they were using limited bulk outreach, which slowed down campaign effectiveness.",
                                        ],
                                    },
                                    {
                                        listItem: "Hidden charges and poor onboarding with other providers",
                                        nestedListItem: [
                                            "The team previously used a third-party provider, but many promised features were locked behind expensive plans, and hidden costs made the platform unreliable.",
                                        ],
                                    },
                                ]}
                                className="mt-4 bg-gray-100 "
                            />
                            <CaseStudyInformation subTitle={"Initial solutions tried"} className="mt-10" />
                            <p className="mt-2">To compensate, NURA resorted to manual tracking:</p>
                            <FeatureList
                                List={[
                                    {
                                        listItem: "Excel sheets to track activity",
                                        nestedListItem: [
                                            "Every incoming and outgoing WhatsApp message, booking, and follow-up was logged manually in Excel by the customer care team.",
                                        ],
                                    },
                                    {
                                        listItem: "Device linking for supervision",
                                        nestedListItem: [
                                            "The management connected staff WhatsApp accounts to desktops to monitor conversations in real-time, which was a time-consuming and unsustainable process.",
                                        ],
                                    },
                                ]}
                                className="mt-4 bg-gray-100 "
                            />
                            <p className="mt-5">Despite these efforts, efficiency remained low and errors crept in frequently.</p>
                        </section>

                        <section id="Solutions">
                            <CaseStudyInformation subTitle={"Why NURA chose Gallabox"} className="mt-0" />
                            <p className="mt-2">NURA’s search for a transparent, reliable solution ended with Gallabox. They chose Gallabox because:</p>

                            <FeatureList
                                List={[
                                    {
                                        listItem: "Transparent onboarding:",
                                        nestedListItem: [
                                            "They were delighted with the multiple, tailored demos where all workflows were clearly explained and clarity was provided on all features.",
                                        ],
                                    },
                                    {
                                        listItem: "Comprehensive functionality:",
                                        nestedListItem: [
                                            "Unlike competitors, Gallabox offered all critical features — bulk messages, automation, shared inbox — in one platform without hidden charges.",
                                        ],
                                    },
                                    {
                                        listItem: "Tailored use cases:",
                                        nestedListItem: ["Gallabox helped design healthcare-specific automations that addressed NURA’s exact pain points."],
                                    },
                                ]}
                                className="mt-4 bg-gray-100 "
                            />
                            <CaseStudyInformation subTitle={"Goals with Gallabox"} className="mt-12" />
                            <p className="mt-2">NURA’s implemented Gallabox to:</p>
                            <FeatureList
                                List={[
                                    { listItem: "Nurture leads more effectively" },
                                    { listItem: "Streamline patient communication workflows" },
                                    { listItem: "Improve team productivity and reduce manual errors" },
                                ]}
                                className="mt-4 bg-gray-100 "
                            />
                        </section>

                        <section id="Impact">
                            <CaseStudyInformation subTitle={"Impact on their business growth"} className="mt-0" />
                            <p className="mt-2">The results with Gallabox were transformational: </p>

                            <FeatureList
                                List={[
                                    {
                                        listItem: "90% increase in employee productivity, as repetitive manual work was eliminated.",
                                    },
                                    {
                                        listItem: "80% increase in first-time conversions, with faster and personalized responses.",
                                    },
                                    {
                                        listItem: "60% growth in customer satisfaction rates, thanks to reliable, timely communication.",
                                    },
                                    {
                                        listItem: "Noticeable uplift in profitability, even at early adoption stages.",
                                    },
                                ]}
                                className="mt-5 bg-gray-100"
                            />

                            <CaseStudyInformation subTitle={"Building healthcare efficiency through automation"} className="mt-10" />
                            <p className="mt-2">
                                Gallabox provided NURA with the clarity, control, and consistency it was lacking. By streamlining WhatsApp operations, automating bulk
                                communication, and enabling performance monitoring, NURA has positioned itself as a modern, customer-first healthcare provider.
                            </p>
                        </section>
                    </div>
                </div>
                <div>
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <Navbar links={navLinks} />
                    </div>
                </div>
            </section>
            <div className="max-w-6xl mx-auto">
                <section>
                    <h3 className="text-gray-900 mb-10 mt-5 text-left ml-4">Check out other customer stories</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto gap-10 mb-20">
                        {casestudyCardsDetails?.map((data, index) => (
                            <CasestudyCard key={index} {...data} />
                        ))}
                    </div>
                </section>
            </div>
        </NewLayout>
    );
};

export default Nura;
